// ===================================
// PERMISSION UTILITIES - src/utils/permissions.ts
// ===================================
import { User, UserPermissions } from '../types';

/**
 * Parse user permissions from permission string
 */
export function parseUserPermissions(user: User | null): UserPermissions {
  if (!user) {
    return {
      canCreate: false,
      canRead: false,
      canUpdate: false,
      canDelete: false,
      canReadAll: false,
      canModifyAll: false,
      isGodMode: false
    };
  }

  const permissions = user.permission.toLowerCase().split(',').map(p => p.trim());
  const isGodMode = user.role_name === 'General Manager' || permissions.includes('modifyall');
  
  return {
    canCreate: permissions.includes('create'),
    canRead: permissions.includes('read') || permissions.includes('readall'),
    canUpdate: permissions.includes('update'),
    canDelete: permissions.includes('delete') && (user.role_name === 'Manager' || user.role_name === 'Owner' || isGodMode),
    canReadAll: permissions.includes('readall') || isGodMode,
    canModifyAll: permissions.includes('modifyall') || isGodMode,
    isGodMode
  };
}

/**
 * Check if user can perform specific action
 */
export function canUserPerformAction(user: User | null, action: 'create' | 'read' | 'update' | 'delete'): boolean {
  const permissions = parseUserPermissions(user);
  
  switch (action) {
    case 'create':
      return permissions.canCreate;
    case 'read':
      return permissions.canRead;
    case 'update':
      return permissions.canUpdate;
    case 'delete':
      return permissions.canDelete;
    default:
      return false;
  }
}

/**
 * Check if user can access business data
 */
export function canUserAccessBusiness(user: User | null, businessId: number | null): boolean {
  if (!user) return false;
  
  const permissions = parseUserPermissions(user);
  
  // God mode users can access all businesses
  if (permissions.isGodMode) return true;
  
  // Users can access their own business
  if (businessId === null || businessId === user.business_id) return true;
  
  return false;
}

/**
 * Get user's accessible business IDs
 */
export function getUserAccessibleBusinessIds(user: User | null): number[] {
  if (!user) return [];
  
  const permissions = parseUserPermissions(user);
  
  // God mode users get all businesses (would need API call to get all business IDs)
  if (permissions.isGodMode) {
    // For now, return empty array - frontend should handle this case
    // In a real app, you'd call an API to get all business IDs
    return [];
  }
  
  // Regular users get their own business only
  return [user.business_id];
}

/**
 * Filter business options based on user permissions
 */
export function getBusinessOptionsForUser(user: User | null, allBusinesses: Array<{id: number, name: string}>): Array<{id: number, name: string}> {
  if (!user) return [];
  
  const permissions = parseUserPermissions(user);
  
  // God mode users see all businesses
  if (permissions.isGodMode) {
    return allBusinesses;
  }
  
  // Regular users see only their business
  return allBusinesses.filter(business => business.id === user.business_id);
}

/**
 * Get permission-based UI labels
 */
export function getPermissionLabels(user: User | null) {
  const permissions = parseUserPermissions(user);
  
  return {
    createButton: permissions.canCreate ? 'Create Topic' : 'Create (No Permission)',
    editButton: permissions.canUpdate ? 'Edit' : 'Edit (No Permission)',
    deleteButton: permissions.canDelete ? 'Delete' : 'Delete (Manager+ Only)',
    accessScope: permissions.isGodMode ? 'All Businesses' : `Limited to ${user?.current_business || 'Current Business'}`
  };
}

/**
 * Check if user should see global business filter
 */
export function shouldShowBusinessFilter(user: User | null): boolean {
  const permissions = parseUserPermissions(user);
  return permissions.isGodMode;
}

/**
 * Get default business ID for operations
 */
export function getDefaultBusinessId(user: User | null): number | null {
  if (!user) return null;
  
  const permissions = parseUserPermissions(user);
  
  // God mode users don't have a default business (they can choose)
  if (permissions.isGodMode) return null;
  
  // Regular users default to their business
  return user.business_id;
}

/**
 * Validate if user can modify a topic
 */
export function canUserModifyTopic(user: User | null, topic: { business_id: number | null }): boolean {
  if (!user) return false;
  
  const permissions = parseUserPermissions(user);
  
  // God mode users can modify any topic
  if (permissions.isGodMode) return true;
  
  // Users can modify topics in their business
  if (topic.business_id === null || topic.business_id === user.business_id) {
    return permissions.canUpdate;
  }
  
  return false;
}

/**
 * Validate if user can delete a topic
 */
export function canUserDeleteTopic(user: User | null, topic: { business_id: number | null }): boolean {
  if (!user) return false;
  
  const permissions = parseUserPermissions(user);
  
  // God mode users can delete any topic
  if (permissions.isGodMode) return true;
  
  // Users can delete topics in their business if they have delete permission
  if (topic.business_id === null || topic.business_id === user.business_id) {
    return permissions.canDelete;
  }
  
  return false;
}

/**
 * Get role-specific styling for UI elements
 */
export function getRoleStyles(user: User | null) {
  if (!user) return { badgeColor: 'gray', textColor: 'gray-600' };
  
  const permissions = parseUserPermissions(user);
  
  if (permissions.isGodMode) {
    return { badgeColor: 'purple', textColor: 'purple-600' };
  }
  
  if (user.is_admin) {
    return { badgeColor: 'red', textColor: 'red-600' };
  }
  
  if (permissions.canDelete) {
    return { badgeColor: 'blue', textColor: 'blue-600' };
  }
  
  return { badgeColor: 'gray', textColor: 'gray-600' };
}